import { ApiLocation } from "@incendium/api";
import {
  Box,
  Stack,
  Typography,
  styled,
  Alert,
} from "@mui/material";
import LocationAccordionItem from "features/location/components/LocationAccordionItem";
import Loading from "Components/Loading/Loading";
import { useIsMobile } from "Hooks/useIsMobile";

const AccordionListContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.common.white,
}));

interface ILocationsAccordionListProps {
  locations: ApiLocation[];
  loading?: boolean;
  title?: string;
  subtitle?: string;
  onEdit?: (location: ApiLocation) => void;
  onDelete?: (location: ApiLocation) => void;
  onSave?: (location: ApiLocation) => Promise<void>;
  showDelete?: boolean;
  /** When true, only the accordion rows scroll; header stays fixed */
  scrollable?: boolean;
}

function LocationsAccordionList({
  locations,
  loading,
  title,
  subtitle,
  onEdit,
  onDelete,
  onSave,
  showDelete = true,
  scrollable = false,
}: ILocationsAccordionListProps) {
  const isMobile = useIsMobile();
  if (loading) {
    return <Loading />;
  }

  const header = (title || subtitle) && (
    <Box mb={3} flexShrink={0}>
      {title && (
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {isMobile && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Note: You can only edit and delete page groups on the desktop version.
        </Alert>
      )}
    </Box>
  );

  const listBody =
    locations.length === 0 ? (
      <Typography variant="body2" color="text.secondary" py={4} px={2}>
        No page groups detected yet.
      </Typography>
    ) : (
      <AccordionListContainer>
        <Stack>
          {locations.map((location) => (
            <LocationAccordionItem
              key={location.id}
              location={location}
              onEdit={onEdit}
              onDelete={onDelete}
              onSave={onSave}
              showDelete={showDelete}
            />
          ))}
        </Stack>
      </AccordionListContainer>
    );

  if (scrollable) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {header}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{listBody}</Box>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      {listBody}
    </Box>
  );
}

export default LocationsAccordionList;
